// utils/ExportAllDataToExcel.tsx
/**
 * The band's Export button: gathers the dashboard's figures and the context they were drawn for,
 * builds the report (utils/excelReport.ts) and downloads it. The workbook's design lives there.
 */
import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useGraphData } from '@/contexts';
import { downloadBlob } from '@/utils/exportImage';
import { bandIconButtonSx } from '@/styles/base/band';
import { asLocalDate, buildWorkbook, type ReportContext } from '@/utils/excelReport';

interface ExportButtonProps {
    disabled?: boolean;
    /** Whose figures, for which dates, under which filters: the cover sheet states them */
    context: Omit<ReportContext, 'exportedAt' | 'logo'>;
}

const LOGO_URL = '/images/infinitum-logo.png';

/** The logo as a data URL with its size, for the cover; none if it cannot be fetched */
const loadLogo = async (): Promise<ReportContext['logo']> => {
    try {
        const blob = await (await fetch(LOGO_URL)).blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result as string);
            r.onerror = reject;
            r.readAsDataURL(blob);
        });
        const { width, height } = await createImageBitmap(blob);
        return { dataUrl, width, height };
    } catch {
        return undefined;
    }
};

const slug = (s: string) => s.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
const ymd = (v: unknown) => {
    const d = asLocalDate(v);
    return d
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : '';
};

export const IconExportButton = ({ disabled = false, context }: ExportButtonProps) => {
    const data = useGraphData();

    const handleExport = async (): Promise<void> => {
        if (disabled) return;
        const excelModule = await import('exceljs');
        const ExcelJS = ('default' in excelModule ? excelModule.default : excelModule) as typeof import('exceljs');
        const workbook = buildWorkbook(ExcelJS, data as never, {
            ...context,
            logo: await loadLogo(),
            exportedAt: new Date(),
        });
        const from = ymd(context.startDate);
        const to = ymd(context.endDate);
        const range = from && to ? `_${from}_to_${to}` : '';
        const filename = `Infinitum_${slug(context.client) || 'report'}${range}.xlsx`;
        const buffer = await workbook.xlsx.writeBuffer();
        downloadBlob(
            filename,
            new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        );
    };

    React.useEffect(() => {
        window.exportAllDataToExcel = handleExport;
        return () => {
            delete window.exportAllDataToExcel;
        };
    });

    return (
        <Tooltip title="Export all data to Excel">
            <span>
                {' '}
                {/* Wrapper to handle disabled tooltip behavior */}
                <IconButton
                    onClick={handleExport}
                    disabled={disabled}
                    data-testid="export-to-excel-button"
                    sx={bandIconButtonSx}
                >
                    <FileDownloadIcon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
};
